import { processByQuery } from '@pn/core/domain/data';
import type { GeoJsonDataSource } from '@pn/core/domain/workspace';
import { handleError } from '@pn/core/errors/handleError';
import type { IDataProvider } from '@pn/core/services/data/ports';
import { getMapping, setMapping } from '@pn/core/storage/workspace/mappings';
import { isValidHttpUrl } from '@pn/core/utils/validation';
import { pnApiClient } from '@pn/services/api/pnApiClient';
import { generateMappingFromGeoJsonData } from '@pn/services/local/data/generateMappingFromGeoJsonData';
import { geoJsonDataItemMapper } from '@pn/services/local/data/geoJsonDataItemMapper';
import { geoJsonToData } from '@pn/services/local/data/geoJsonToDataConverter';
import type { GeoJsonDataItem } from '@pn/services/local/data/types';
import { checkGeoJsonForMixedTypes } from '@pn/services/local/data/utils';
import { notificationService } from '@pn/services/notifications';
import { isGeoJSONFeatureCollection } from '@pn/services/utils/geojson';
import { isEmpty, isNil } from 'lodash-es';

const cacheStorage = new Map<string, GeoJSON.Feature[]>();
const cache = {
  has: (layerId: string) => cacheStorage.has(layerId),
  get: (layerId: string) => cacheStorage.get(layerId),
  set: (layerId: string, data: GeoJSON.Feature[]) =>
    cacheStorage.set(layerId, data),
};

export const geoJsonDataProvider: IDataProvider<GeoJsonDataSource> = (
  dataSource
) => ({
  getDataMapping: async (item) => {
    const existingMapping = getMapping(item.dataType);
    if (!isNil(existingMapping)) return existingMapping;

    try {
      const geoJsonDataItems = await getGeoJsonDataItems(
        item.dataType,
        dataSource
      );
      const mapping = generateMappingFromGeoJsonData(geoJsonDataItems);

      setMapping(item.dataType, mapping);

      return mapping;
    } catch (error) {
      handleError({
        error,
        notify: 'Failed to retrieve GeoJSON data',
      });

      return {};
    }
  },

  getDataByQuery: async ({ query, mapping }) => {
    if (isEmpty(mapping)) {
      return { data: [], totalCount: 0 };
    }

    const geoJsonDataItems = await getGeoJsonDataItems(
      query.dataType,
      dataSource
    );
    const dataItems = geoJsonDataItems.map(
      geoJsonDataItemMapper(mapping).toDomainDataItem
    );

    return processByQuery({ localData: dataItems, queryType: 'scroll', query });
  },

  streamDataByQuery: async ({ query, mapping }) => {
    if (isEmpty(mapping)) {
      return { data: [], totalCount: 0 };
    }

    const geoJsonDataItems = await getGeoJsonDataItems(
      query.dataType,
      dataSource
    );
    const dataItems = geoJsonDataItems.map(
      geoJsonDataItemMapper(mapping).toDomainDataItem
    );

    const { data } = processByQuery({
      localData: dataItems,
      queryType: 'stream',
      query,
    });

    return { data, totalCount: data.length };
  },

  getItemById: async ({ id, layerId, mapping }) => {
    const geoJsonDataItems = await getGeoJsonDataItems(layerId, dataSource);
    const dataItems = geoJsonDataItems.map(
      geoJsonDataItemMapper(mapping).toDomainDataItem
    );

    return dataItems.find((dataItem) => dataItem._id === id);
  },
});

async function getGeoJsonDataItems(
  layerId: string,
  dataSource: GeoJsonDataSource
): Promise<GeoJsonDataItem[]> {
  let features: GeoJSON.Feature[];

  if ('data' in dataSource) {
    features = dataSource.data.features;
  } else if (cache.has(layerId)) {
    features = cache.get(layerId)!;
  } else {
    features = await retrieveAndProcessGeoJson(layerId, dataSource.url);
  }

  const geoJsonDataItems = geoJsonToData({
    type: 'FeatureCollection',
    features,
  });

  return geoJsonDataItems;
}

async function retrieveAndProcessGeoJson(
  layerId: string,
  url: string
): Promise<GeoJSON.Feature[]> {
  if (isValidHttpUrl(url)) {
    const response = await fetch(url);
    const geojson = (await response.json()) as GeoJSON.FeatureCollection;

    cache.set(layerId, geojson.features);

    return geojson.features;
  }

  const response = await pnApiClient.request({ url });
  if (!isGeoJSONFeatureCollection(response)) {
    throw new Error(
      'Failed to download layer data. Invalid GeoJSON.FeatureCollection'
    );
  }

  cache.set(layerId, response.features);

  if (checkGeoJsonForMixedTypes(response.features)) {
    notificationService.warning(
      'Mixed geometry types detected. Some of the layer features will not be displayed on the map'
    );
  }

  return response.features;
}
